/**
 * BoxSearchTab — Searchable, filterable, paginated box search with export & print.
 * Extracted from Dashboard.tsx for maintainability.
 */
import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Loader2, Download, Printer, Filter, RefreshCw, Calendar } from 'lucide-react';
import inventoryService, { BoxSearchParams, BoxSearchResponse } from '../../services/inventoryService';
import { formatGS1Barcode } from '../../utils/barcodeUtils';
import { useLanguage } from '../../i18n/LanguageContext';
import { getDateCode } from '../../utils/dateUtils';
import DatePickerDropdown from '../../components/DatePickerDropdown';
import TimePickerDropdown from '../../components/TimePickerDropdown';

const BoxSearchTab: React.FC = () => {
  const { t } = useLanguage();

  const [filters, setFilters] = useState<BoxSearchParams>({
    page: 1,
    limit: 50,
  });
  const [appliedFilters, setAppliedFilters] = useState<BoxSearchParams>({
    page: 1,
    limit: 50,
  });

  // Fetch item numbers for dropdown
  const { data: itemNumbers = [] } = useQuery(
    ['box-item-numbers'],
    () => inventoryService.getBoxItemNumbers(),
    { staleTime: 60000 }
  );

  // Fetch boxes with applied filters
  const { data: searchResult, isLoading } = useQuery<BoxSearchResponse>(
    ['box-search', appliedFilters],
    () => inventoryService.searchBoxes(appliedFilters),
    { keepPreviousData: true }
  );

  const handleSearch = () => {
    setAppliedFilters({ ...filters, page: 1 });
  };

  // Auto-apply filters as the user types/selects (debounced). The Refresh
  // button still works but is generally unnecessary now — typing in Date Code,
  // or picking Item / Packed From / Packed To, filters automatically.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters({ ...filters, page: 1 });
    }, 400);
    return () => clearTimeout(timer);
  }, [filters]);

  const handleExport = async () => {
    try {
      const blob = await inventoryService.exportBoxSearch(appliedFilters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `box-search-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handlePrint = () => {
    if (!searchResult?.boxes?.length) return;

    // Create a new window with just the table for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const tableRows = searchResult.boxes.map(box => `
      <tr>
        <td style="border: 1px solid #ddd; padding: 8px;">${box.box_number}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${box.item_number}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${box.product_name || ''}${box.chinese_name ? `<br><small style="color: #666;">${box.chinese_name}</small>` : ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-family: monospace;">${parseFloat(String(box.actual_weight)).toFixed(2)}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${box.lot_number || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${box.packed_at ? new Date(box.packed_at).toLocaleString() : '-'}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${box.operator_name || '-'}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${box.packer_name || '-'}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${box.session_number || '-'}</td>
        <td style="border: 1px solid #ddd; padding: 8px; font-family: monospace; font-size: 11px;">${formatGS1Barcode(box.barcode_number)}</td>
      </tr>
    `).join('');

    // Build filters display
    const filterParts: string[] = [];
    if (appliedFilters.item_number) {
      const item = itemNumbers.find(i => i.item_number === appliedFilters.item_number);
      filterParts.push(`<strong>Item:</strong> ${appliedFilters.item_number}${item?.product_name ? ` - ${item.product_name}` : ''}`);
    }
    if (appliedFilters.date_code) {
      filterParts.push(`<strong>Date Code:</strong> ${appliedFilters.date_code}`);
    }
    if (appliedFilters.packed_from) {
      filterParts.push(`<strong>From:</strong> ${appliedFilters.packed_from}`);
    }
    if (appliedFilters.packed_to) {
      filterParts.push(`<strong>To:</strong> ${appliedFilters.packed_to}`);
    }
    const filtersHtml = filterParts.length > 0
      ? `<div class="filters">${filterParts.join(' &nbsp;&nbsp; ')}</div>`
      : '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Box Search Results</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { font-size: 18px; margin-bottom: 10px; }
          .filters { margin-bottom: 10px; font-size: 13px; color: #444; }
          .summary { margin-bottom: 15px; font-size: 14px; }
          table { border-collapse: collapse; width: 100%; font-size: 12px; }
          th { background-color: #2563eb; color: black; padding: 10px 8px; text-align: left; border: 1px solid #2563eb; }
          td { border: 1px solid #ddd; padding: 8px; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          @media print {
            body { margin: 0; }
            h1 { font-size: 16px; }
          }
        </style>
      </head>
      <body>
        <h1>Box Search Results - ${new Date().toLocaleDateString()}</h1>
        ${filtersHtml}
        <div class="summary">
          <strong>Total Boxes:</strong> ${searchResult.summary?.total_boxes?.toLocaleString() || 0} &nbsp;&nbsp;
          <strong>Total Weight:</strong> ${searchResult.summary?.total_weight?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'} lbs
        </div>
        <table>
          <thead>
            <tr>
              <th>Box #</th>
              <th>Item No.</th>
              <th>Product Name</th>
              <th style="text-align: right;">Weight</th>
              <th>Date Code</th>
              <th>Packed At</th>
              <th>Operator</th>
              <th>Packer</th>
              <th>Session</th>
              <th>Barcode</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handlePageChange = (newPage: number) => {
    setAppliedFilters({ ...appliedFilters, page: newPage });
  };

  const totalPages = searchResult ? Math.ceil(searchResult.total / (appliedFilters.limit || 50)) : 0;
  const [showFilters, setShowFilters] = useState(true);

  const hasActiveFilters = () => {
    return !!(filters.item_number || filters.date_code || filters.packed_from || filters.packed_to);
  };

  return (
    <>
      {/* Filters Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Filter className="w-4 h-4 text-gray-500" />
            {t('common.filters') || 'Filters'}
          </button>
          {hasActiveFilters() && (
            <button
              onClick={() => {
                setFilters({ page: 1, limit: 50 });
                setAppliedFilters({ page: 1, limit: 50 });
              }}
              className="inline-flex items-center gap-1 px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <span className="text-gray-400">×</span>
              {t('common.clear') || 'Clear'}
            </button>
          )}
          <button
            onClick={handleSearch}
            disabled={isLoading}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            title={t('common.refresh') || 'Refresh'}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handlePrint}
            disabled={!searchResult?.boxes?.length}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Printer className="w-4 h-4 mr-2" />
            {t('common.print') || 'Print'}
          </button>
          <button
            onClick={handleExport}
            disabled={!searchResult?.boxes?.length}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4 mr-2" />
            {t('common.export') || 'Export'}
          </button>
        </div>
      </div>

      {/* Filters Panel - collapsible */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Item dropdown */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('boxSearch.item')}</label>
              <select
                value={filters.item_number || ''}
                onChange={(e) => setFilters({ ...filters, item_number: e.target.value || undefined })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('boxSearch.allItems')}</option>
                {itemNumbers.map((item) => (
                  <option key={item.item_number} value={item.item_number}>
                    {item.item_number} - {item.product_name}{item.chinese_name ? ` (${item.chinese_name})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Code - calendar picker that displays as YYDDD */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('boxSearch.dateCode')}</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={filters.date_code || ''}
                  onChange={(e) => setFilters({ ...filters, date_code: e.target.value || undefined })}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  placeholder="YYDDD"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <DatePickerDropdown
                  selected={null}
                  onSelect={(date) => setFilters({ ...filters, date_code: getDateCode(date) })}
                >
                  {({ onClick }) => (
                    <button type="button" onClick={onClick} className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                      <Calendar className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </DatePickerDropdown>
              </div>
            </div>

            {/* Packed From */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('boxSearch.packedFrom')}</label>
              <TimePickerDropdown
                value={filters.packed_from || ''}
                onChange={(v) => setFilters({ ...filters, packed_from: v || undefined })}
              />
            </div>

            {/* Packed To */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('boxSearch.packedTo')}</label>
              <TimePickerDropdown
                value={filters.packed_to || ''}
                onChange={(v) => setFilters({ ...filters, packed_to: v || undefined })}
              />
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      {searchResult?.summary && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6 flex gap-8">
          <div>
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('boxSearch.totalBoxes')}:</span>
            <span className="ml-2 text-lg font-semibold text-gray-900 dark:text-white">
              {searchResult.summary.total_boxes.toLocaleString()}
            </span>
          </div>
          <div>
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('boxSearch.totalWeight')}:</span>
            <span className="ml-2 text-lg font-semibold text-gray-900 dark:text-white">
              {searchResult.summary.total_weight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('boxSearch.lbs')}
            </span>
          </div>
        </div>
      )}

      {/* Results Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  Box #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('boxSearch.itemNumber')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('boxSearch.productName')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('boxSearch.weight')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('boxSearch.dateCode')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('boxSearch.packedAt')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('boxSearch.operator')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('boxSearch.packer')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('boxSearch.session')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('boxSearch.barcode')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    {t('common.loading')}
                  </td>
                </tr>
              ) : searchResult?.boxes?.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {t('boxSearch.noResults')}
                  </td>
                </tr>
              ) : (
                searchResult?.boxes?.map((box) => (
                  <tr key={box.box_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {box.box_number}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {box.item_number}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      <div>{box.product_name}</div>
                      {box.chinese_name && (
                        <div className="text-xs text-gray-400">{box.chinese_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-mono text-gray-900 dark:text-white">
                      {parseFloat(String(box.actual_weight)).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {box.lot_number}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {box.packed_at ? new Date(box.packed_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {box.operator_name || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {box.packer_name || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {box.session_number || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 font-mono text-xs">
                      {formatGS1Barcode(box.barcode_number)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {searchResult && searchResult.total > (appliedFilters.limit || 50) && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('common.showing')} {((appliedFilters.page || 1) - 1) * (appliedFilters.limit || 50) + 1} - {Math.min((appliedFilters.page || 1) * (appliedFilters.limit || 50), searchResult.total)} {t('common.of')} {searchResult.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange((appliedFilters.page || 1) - 1)}
                disabled={(appliedFilters.page || 1) <= 1}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 dark:bg-gray-800"
              >
                {t('common.previous')}
              </button>
              <span className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300">
                {appliedFilters.page || 1} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange((appliedFilters.page || 1) + 1)}
                disabled={(appliedFilters.page || 1) >= totalPages}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 dark:bg-gray-800"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BoxSearchTab;
